/**
 * Client-side auth gate.
 *
 *   ensureAuth()
 *     → resolves to the Me payload if the session is valid
 *     → redirects to /login.html (preserving `?redirect=<current-url>`) otherwise
 *
 * Also validates tenant membership: if the URL names a tenant slug but the
 * signed-in user isn't a member, we redirect to `/` (the picker).
 *
 * The admin shell ships with `<html class="auth-pending">` and CSS that hides
 * all body content until that class is removed. Successful auth strips the
 * class so the dashboard chrome reveals; unauthenticated visitors are
 * redirected away with the class still set, so they never see the admin UI.
 */

import { authApi, type Me, ApiError } from './api';
import { resolveTenantSlug } from './tenant';

/** Remove the `auth-pending` gate class on <html> so the chrome becomes visible. */
function revealApp(): void {
  document.documentElement.classList.remove('auth-pending');
}

export async function ensureAuth(): Promise<Me> {
  let me: Me;
  try {
    me = await authApi.me();
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      const redirect = encodeURIComponent(window.location.pathname + window.location.search);
      // Leave `auth-pending` class in place — page is navigating to /login.html and the
      // dashboard chrome must never render to an unauthenticated visitor.
      window.location.replace(`/login.html?redirect=${redirect}`);
      // Throw so callers don't continue rendering.
      throw err;
    }
    // Non-auth error (network down, 500, etc): reveal so the user isn't stuck staring
    // at a spinner forever. Caller's catch handler is responsible for the visible state.
    revealApp();
    throw err;
  }

  const memberships = me.memberships ?? [];
  const tenant = resolveTenantSlug();
  const isPlatformAdmin = me.user?.platform_admin === 1;
  const pathname = window.location.pathname;
  const onPlatformPage =
    pathname === '/platform.html' ||
    pathname === '/platform' ||
    pathname.startsWith('/platform.html');

  // Platform admins land on /platform.html when they hit the root with no
  // tenant slug in the URL. This is what the @LB-A1 test expects: navigate to
  // `/` and see a "+ New Client" button. Without this, single-membership admins
  // would be bounced to their tenant dashboard and zero-membership admins would
  // see the "No workspaces" empty state — neither of which exposes the wizard.
  if (!tenant && isPlatformAdmin && !onPlatformPage) {
    // Leave `auth-pending` set — page is navigating; no flash of the picker.
    // Use `/platform` (no .html) because Pages 308-redirects /platform.html → /platform.
    window.location.replace('/platform');
    throw new Error('redirecting platform admin to /platform');
  }

  if (tenant) {
    const ok =
      memberships.some((m) => m.tenant_slug === tenant.slug) ||
      me.user?.platform_admin === 1;
    if (!ok) {
      // User is signed in but not a member of this tenant.
      revealApp();
      document.body.innerHTML = `
        <div style="max-width:480px;margin:15vh auto;padding:2rem;font-family:Inter,sans-serif;background:#fff;border:1px solid #e2e6ed;border-radius:12px;box-shadow:0 10px 25px rgba(0,0,0,0.08);text-align:center">
          <h1 style="margin:0 0 0.5rem 0;font-size:1.5rem;color:#1a1d23">Access denied</h1>
          <p style="color:#6b7280;margin:0 0 1.5rem 0">You are signed in as <strong>${escapeHtml(me.user?.email ?? 'unknown')}</strong> but you are not a member of <strong>${escapeHtml(tenant.slug)}</strong>.</p>
          <a href="/" style="display:inline-block;background:#2563eb;color:#fff;padding:0.6rem 1.2rem;border-radius:8px;text-decoration:none;font-weight:600">Back to dashboard</a>
          <a id="access-denied-signout" href="#signout" style="display:inline-block;margin-left:0.5rem;color:#2563eb;padding:0.6rem 1.2rem;text-decoration:none">Sign out</a>
        </div>`;

      // Wire the Sign out link to call logout API.
      const signoutLink = document.getElementById('access-denied-signout');
      if (signoutLink) {
        signoutLink.addEventListener('click', async (e) => {
          e.preventDefault();
          try {
            await authApi.logout();
          } catch {
            // Ignore.
          }
          window.location.href = '/login.html';
        });
      }

      throw new Error('not a member');
    }
  } else if (memberships.length === 1) {
    // No slug in URL but user has exactly one tenant — redirect into it.
    const slug = memberships[0].tenant_slug;
    if (!window.location.pathname.startsWith(`/t/${slug}`)) {
      // Leave `auth-pending` set — page is navigating, no need to reveal the
      // platform-level chrome on the way out.
      window.location.replace(`/t/${slug}/`);
      throw new Error('redirecting to single-tenant home');
    }
  } else if (memberships.length === 0) {
    revealApp();
    document.body.innerHTML = `
      <div style="max-width:480px;margin:15vh auto;padding:2rem;font-family:Inter,sans-serif;background:#fff;border:1px solid #e2e6ed;border-radius:12px;box-shadow:0 10px 25px rgba(0,0,0,0.08);text-align:center">
        <h1 style="margin:0 0 0.5rem 0;font-size:1.5rem;color:#1a1d23">No workspaces</h1>
        <p style="color:#6b7280;margin:0 0 1.5rem 0">You are signed in as <strong>${escapeHtml(me.user?.email ?? 'unknown')}</strong> but you don't belong to any tenant yet. Ask your admin to send you an invite.</p>
        <a id="no-memberships-signout" href="#signout" style="display:inline-block;color:#2563eb;padding:0.6rem 1.2rem;text-decoration:none">Sign out</a>
      </div>`;

    // Wire the Sign out link to call logout API.
    const signoutLink = document.getElementById('no-memberships-signout');
    if (signoutLink) {
      signoutLink.addEventListener('click', async (e) => {
        e.preventDefault();
        try {
          await authApi.logout();
        } catch {
          // Ignore.
        }
        window.location.href = '/login.html';
      });
    }

    throw new Error('no memberships');
  }
  // Happy path: authenticated, membership OK (or platform admin). Reveal the chrome
  // so admin.ts can paint into the existing DOM (or render the picker when
  // memberships.length > 1 with no slug).
  revealApp();
  return me;
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}
